import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Page from '../../lib/page';
import ChatBot from './ChatBot';
import poll from '../../lib/poll';
import { elmoTreatmentNames, isPartOfElmoExpTreatment } from '../../lib/experiments-helper';
import { nodeChatOnPageValidator } from '../../lib/node-chat-on-page-validator';
import fireImpressionFptiForChatLoadError from '../../lib/fire-impression-fpti-for-chat-load-error';
import { CHAT_ENTRY_POINTS } from '../../common/commonConstants';

const ChatBotAssistant = function (props) {
  const { link, display, topicRefId, chatBotClicked, chatPillOcpParams, isChatBotAsyncUser, subject } = props;

  const CHATBOT_VARIATION_V1 = 'v1';
  const CHATBOT_VARIATION_V2 = 'v2';
  const CHATBOT_SESSIONTYPE = 'chatBot';

  const pageName = Page.getPageName();

  // check - elmo treatments availability
  const treatmentsAvailable = Page.elmoTreatments();
  const isPartOfChatBotAssistantShortMsgTreatment = isPartOfElmoExpTreatment(
    treatmentsAvailable,
    elmoTreatmentNames.chatBotAssistantShortMessageExp,
  );
  const isPartOfChatBotAssistantLongMsgTreatment = isPartOfElmoExpTreatment(
    treatmentsAvailable,
    elmoTreatmentNames.chatBotAssistantLongMessageExp,
  );
  const chatBotDisplayAfterPostEligibility = useSelector((state) => state.postChatBotEligibility.chatBotDisplay);

  /**
   * Check to see which treatment for the chatbotAssitant is available.
   * long message is chatbot variation v1
   * short message is chatbot variation v2
   */
  const getChatBotVariation = () => {
    if (isPartOfChatBotAssistantLongMsgTreatment) {
      return CHATBOT_VARIATION_V1;
    }
    if (isPartOfChatBotAssistantShortMsgTreatment) {
      return CHATBOT_VARIATION_V2;
    }

    return '';
  };

  const chatBotVariation = getChatBotVariation();

  // launch chatbot assistant if eligible
  useEffect(() => {
    if (
      Page.isChatBotEnabled() &&
      !Page.isAssistantPromptEnabled() &&
      chatBotDisplayAfterPostEligibility &&
      chatBotVariation
    ) {
      // Waits at least 3.20 secs based on current retries = 5 and delay = 200 ms
      poll(nodeChatOnPageValidator, 5, 200)
        .then(() => {
          const options = {};
          options.sessionType = isChatBotAsyncUser ? 'async' : CHATBOT_SESSIONTYPE;
          options.topicName = topicRefId;
          options.page = pageName;
          options.promptVariation = chatBotVariation;
          options.subject = subject || '';
          options.entryPoint = CHAT_ENTRY_POINTS.CONTACT_PAGE_MESSAGE_US_PROMPT;

          // eslint-disable-next-line no-undef
          nodeChat.showPrompt(options);
        })
        .catch(() => fireImpressionFptiForChatLoadError());
    }
  });

  // If elmo treatment is not available then fallback to the existing green prompt flow for chatbot
  if (!chatBotVariation && !Page.isAssistantPromptEnabled()) {
    return (
      <ChatBot
        link={link}
        id={CHATBOT_SESSIONTYPE}
        display={display}
        topicRefId={topicRefId}
        subject={subject}
        chatBotClicked={chatBotClicked}
        chatPillOcpParams={chatPillOcpParams}
        isChatBotAsyncUser={isChatBotAsyncUser}
      />
    );
  }

  return null;
};

ChatBotAssistant.propTypes = {
  link: PropTypes.string.isRequired,
  display: PropTypes.bool.isRequired,
  chatBotClicked: PropTypes.func,
  topicRefId: PropTypes.string,
  subject: PropTypes.string,
  isChatBotAsyncUser: PropTypes.bool.isRequired,
  chatPillOcpParams: PropTypes.string,
};

ChatBotAssistant.defaultProps = {
  link: '',
  display: false,
  topicRefId: 'GENPOOL', // GENPOOL is generic topic id
  chatBotClicked: () => {},
};

export default ChatBotAssistant;
